import { cn } from '@design-system';
import { PageLink } from '@/components';
import {
  OnboardingCarePhasesPhaseOne,
  OnboardingCareSettingsPhaseOne,
  OnboardingCareSettingsPhaseTwo,
  OnboardingCareSettingsPhaseThree,
  OnboardingFamilyCaregivingPhaseOne,
  OnboardingFamilyCaregivingPhaseTwo,
  OnboardingFamilyCaregivingPhaseThree,
  OnboardingFundingSources,
  OnboardingFundingCoverage,
  OnboardingDownloadReport,
  OnboardingOurPredictions,
  OnboardingPredictedCareNeeds,
  OnboardingPolicyApplicationDashboard,
} from '@/pages';

import { type FunctionComponent } from 'react';
import { useSelector } from 'react-redux';

import { selectClient, selectSession, selectFeatureFlags } from '@/model';
import {
  clientHasPolicyFunding,
  clientHasAnnuityFunding,
  clientHasSelfFunding,
} from '@/features/client-onboarding';
import {
  useSlideConfig,
  useIsMobileMenuOpen,
  useMobileMenuActions,
} from '@/features/client-onboarding/stores/onboarding-navigation-store';
import { useOnboardingNavigation } from '@/features/client-onboarding/hooks/use-onboarding-navigation';
import { SLIDE_TITLES, isD2CPaywallLocked } from '@/features/client-onboarding/lib/slide-config';
import { CareEnvironment, CarePhase, SessionType, isPolicyEligibleForApplication } from '@shared';

function isNestedSlide(slide: FunctionComponent<ClientIdProps>) {
  return (
    slide === OnboardingCareSettingsPhaseOne ||
    slide === OnboardingCareSettingsPhaseTwo ||
    slide === OnboardingCareSettingsPhaseThree ||
    slide === OnboardingFamilyCaregivingPhaseOne ||
    slide === OnboardingFamilyCaregivingPhaseTwo ||
    slide === OnboardingFamilyCaregivingPhaseThree
  );
}

function getMenuFilteredSlides(
  slides: FunctionComponent<ClientIdProps>[],
  client: Client,
): FunctionComponent<ClientIdProps>[] {
  if (client.intakeSurvey?.clientHasStartedLtc) {
    return slides.filter(
      slide => slide !== OnboardingOurPredictions && slide !== OnboardingPredictedCareNeeds,
    );
  }
  return slides;
}

function isSlideDisabled(
  slide: FunctionComponent<ClientIdProps>,
  index: number,
  selectedSlides: FunctionComponent<ClientIdProps>[],
  careEnvironmentSelections: Partial<Record<CarePhase, CareEnvironment | null>> | undefined,
  client: Client,
) {
  for (let i = 0; i < index; i++) {
    const prevSlide = selectedSlides[i];

    if (
      prevSlide === OnboardingCarePhasesPhaseOne &&
      !careEnvironmentSelections?.[CarePhase.earlyCare]
    ) {
      if (slide === OnboardingCareSettingsPhaseOne) return false;
      return true;
    }

    if (
      prevSlide === OnboardingCareSettingsPhaseOne &&
      !careEnvironmentSelections?.[CarePhase.moderateCare]
    ) {
      if (slide === OnboardingCareSettingsPhaseTwo) return false;
      return true;
    }

    if (
      prevSlide === OnboardingCareSettingsPhaseTwo &&
      !careEnvironmentSelections?.[CarePhase.fullCare]
    ) {
      if (slide === OnboardingCareSettingsPhaseThree) return false;
      return true;
    }
  }

  const fundingSourcesIndex = selectedSlides.indexOf(OnboardingFundingSources);
  if (fundingSourcesIndex !== -1 && index > fundingSourcesIndex) {
    const fundingSourceAdded =
      clientHasPolicyFunding(client) ||
      clientHasAnnuityFunding(client) ||
      clientHasSelfFunding(client);

    if (
      (slide === OnboardingFundingCoverage || slide === OnboardingDownloadReport) &&
      !fundingSourceAdded
    ) {
      return true;
    }

    if (slide === OnboardingPolicyApplicationDashboard) {
      const ltcPolicies = Object.values(
        client.multipleFundingSources.ltcPolicy,
      ) as PolicyFundingSource[];
      const hasShopInsurancePolicy = ltcPolicies.some(ltcPolicy =>
        isPolicyEligibleForApplication(ltcPolicy),
      );
      if (!hasShopInsurancePolicy) return true;
    }
  }

  return false;
}

interface NavigationMenuItemProps {
  slide: FunctionComponent<ClientIdProps>;
  index: number;
  isActive: boolean;
  title: string;
  isNested: boolean;
  isDisabled: boolean;
  isMobile: boolean;
  clientId: string;
  onItemClick?: () => void;
}

function NavigationMenuItem({
  slide,
  index,
  isActive,
  title,
  isNested,
  isDisabled,
  isMobile,
  clientId,
  onItemClick,
}: NavigationMenuItemProps) {
  return (
    <PageLink
      key={index}
      to={slide}
      targetProps={{ clientId }}
      onClick={e => {
        if (isDisabled) {
          e.preventDefault();
          return;
        }
        onItemClick?.();
      }}
      className={cn(
        'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium',
        isMobile
          ? cn(
              isActive && 'bg-gray-100 text-gray-900',
              !isActive && !isDisabled && 'text-gray-700 opacity-75 hover:opacity-75',
              isDisabled && 'cursor-not-allowed text-gray-500',
              isNested && 'pl-6',
            )
          : cn(
              'group/item text-xs',
              isActive && 'text-gray-900',
              !isActive && !isDisabled && 'text-gray-700',
              isDisabled && 'cursor-not-allowed text-gray-500',
              isNested && 'pl-6',
            ),
      )}
    >
      <div
        className={cn(
          'h-[1px] bg-current',
          isNested ? 'w-[5px]' : 'w-[15px]',
          isActive ? 'opacity-100' : 'opacity-75',
        )}
      />
      <span
        className={cn(
          isMobile
            ? cn(
                isActive && 'opacity-100',
                !isActive && !isDisabled && 'opacity-75',
                isDisabled && 'opacity-50',
              )
            : cn(
                'transition-opacity duration-200',
                isActive && 'opacity-100',
                !isActive && !isDisabled && 'opacity-0 group-hover:opacity-75',
                isDisabled && 'opacity-0 group-hover:opacity-50',
              ),
        )}
      >
        {title}
      </span>
    </PageLink>
  );
}

export function OnboardingNavigationMenu() {
  const { slidePage } = useSlideConfig();
  const isMobileMenuOpen = useIsMobileMenuOpen();
  const { closeMobileMenu } = useMobileMenuActions();

  if (!slidePage) return null;

  return (
    <OnboardingNavigationMenuContent
      slidePage={slidePage}
      isMobileMenuOpen={isMobileMenuOpen}
      closeMobileMenu={closeMobileMenu}
    />
  );
}

function OnboardingNavigationMenuContent({
  slidePage,
  isMobileMenuOpen,
  closeMobileMenu,
}: {
  slidePage: FunctionComponent<ClientIdProps>;
  isMobileMenuOpen: boolean;
  closeMobileMenu: () => void;
}) {
  const client = useSelector(selectClient)!;
  const { sessionType, clientStripe } = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;
  const navigation = useOnboardingNavigation({ slidePage });
  const { filteredSlides } = navigation;

  const menuSlides = getMenuFilteredSlides(filteredSlides, client);
  const currentIndex = menuSlides.indexOf(slidePage);

  return (
    <>
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20 xl:hidden"
          onClick={closeMobileMenu}
        />
      )}

      <div
        className={cn(
          'w-70 fixed inset-y-0 right-0 z-50 bg-white transition-transform duration-300 ease-in-out xl:hidden',
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <div className="flex h-full flex-col overflow-y-auto p-4">
          <div className="mt-2 space-y-2">
            {menuSlides.map((slide, index) => (
              <NavigationMenuItem
                key={index}
                slide={slide}
                index={index}
                isActive={index === currentIndex}
                title={SLIDE_TITLES.get(slide) || `Step ${index + 1}`}
                isNested={isNestedSlide(slide)}
                isDisabled={
                  isSlideDisabled(slide, index, menuSlides, client.careEnvironmentSelections, client) ||
                  isD2CPaywallLocked(slide, filteredSlides, isD2C, clientStripe)
                }
                isMobile={true}
                clientId={client.clientId}
                onItemClick={closeMobileMenu}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Vertical Navigation (Desktop) */}
      <div className="fixed left-0 top-1/2 z-10 hidden -translate-y-1/2 xl:block">
        <div className="group flex flex-col justify-center rounded-r-lg bg-transparent p-4 transition-colors duration-200 hover:bg-white hover:shadow-lg">
          {menuSlides.map((slide, index) => (
            <NavigationMenuItem
              key={index}
              slide={slide}
              index={index}
              isActive={index === currentIndex}
              title={SLIDE_TITLES.get(slide) || `Step ${index + 1}`}
              isNested={isNestedSlide(slide)}
              isDisabled={
                isSlideDisabled(slide, index, menuSlides, client.careEnvironmentSelections, client) ||
                isD2CPaywallLocked(slide, filteredSlides, isD2C, clientStripe)
              }
              isMobile={false}
              clientId={client.clientId}
            />
          ))}
        </div>
      </div>
    </>
  );
}
